import { types } from "./mutations";
import { getWidgetData, getPublicWidgetData } from "@/api/widgets";
import { widgetType } from "@/models/Widget.model";
import { createDashboard, updateDashboard } from "@/api/dashboards";

export default {
  initializeDataManager: async (
    { commit },
    { isTemp = false, isPublic = false, magic_link }
  ) => {
    commit(types.SET_TEMP_FLAG, isTemp);
    commit(types.SET_PUBLIC_FLAG, isPublic);
    commit(types.SET_MAGIC_LINK, magic_link);
  },
  /**
   * Makes network request to fetch the widget data and dispatches it to
   * associated data managers
   * @param {*} param0
   * @param {Object} payload
   */
  fetchWidgets: async ({ state, dispatch, commit }, { payload, filters }) => {
    // check if the payload contains any data or not
    if (!payload || payload.length == 0) return null;
    commit(types.SET_REQUEST_PROGRESS, true);

    // if there is a categorical filter set loading as true
    if (payload.find(item => item.widget_type === widgetType.CATEGORICAL)) {
      commit(types.SET_REQUEST_CATEGORICAL_PROGRESS, true);
    }

    // create body for temp widgets or widgets
    const request_data = () => {
      return state.isTemp
        ? {
            temp_widgets: payload,
            ...filters,
          }
        : {
            widgets: payload,
            ...filters,
          };
    };

    let response_data = null;
    try {
      let response = null;
      if (state.isPublic) {
        response = await getPublicWidgetData({
          widgetList: request_data(),
          magicLink: state.magicLink,
        });
      } else {
        response = await getWidgetData(request_data());
      }
      response_data =
        response.data && response.data.data ? response.data.data : null;
      if (!response_data) throw new Error("Invalid response body");
    } catch (error) {
      commit(types.SET_REQUEST_PROGRESS, false);
      commit(types.SET_REQUEST_CATEGORICAL_PROGRESS, false);
    }
    dispatch("DataManager/dispatchWidgetData", response_data, { root: true });
    commit(types.SET_REQUEST_PROGRESS, false);
    commit(types.SET_REQUEST_CATEGORICAL_PROGRESS, false);
  },

  /**
   * Make widget request
   */
  widgetRequest: async ({ state }, { payload, filters }) => {
    const requestData = () => {
      return state.isTemp
        ? {
            temp_widgets: payload,
            ...filters,
          }
        : {
            widgets: payload,
            ...filters,
          };
    };

    try {
      let response = null;

      if (state.isPublic) {
        response = await getPublicWidgetData({
          widgetList: requestData(),
          magicLink: state.magicLink,
        });
      } else {
        response = await getWidgetData(requestData());
      }
      const { data, status } = response;
      if (status === 200) {
        return data.data;
      } else {
        throw new Error("No data found");
      }
    } catch (error) {
      return Promise.reject(error);
    }
  },

  dispatchWidgetData: async ({ dispatch }, widgets) => {
    let geo_data = [],
      time_data = [],
      single_data = [],
      categorical_data = [];

    // If widgets/ request fails, widgets will be empty
    if (!(widgets && widgets.length > 0)) return;

    widgets.forEach(widget => {
      if (!widget.type) return;
      switch (widget.type) {
        case widgetType.GEO:
          geo_data.push(widget);
          break;
        case widgetType.TIME:
          time_data.push(widget);
          break;
        case widgetType.SINGLE:
          single_data.push(widget);
          break;
        case widgetType.CATEGORICAL:
          categorical_data.push(widget);
          break;
        default:
          break;
      }
    });

    // dispatch data to the appropriate managers
    if (single_data && single_data.length > 0) {
      dispatch("LayerManager/loadSingleValues", single_data, { root: true });
    }

    if (geo_data && geo_data.length > 0) {
      // dispatch to map
      dispatch("Map/loadGeoData", geo_data, { root: true });
    }

    if (time_data && time_data.length > 0) {
      // dispatch to time
      dispatch("TimeManager/loadTimeData", time_data, { root: true });
    }

    if (categorical_data && categorical_data.length > 0) {
      dispatch("FilterManager/loadFilterData", categorical_data, {
        root: true,
      });
    }
  },
  /**
   * Create console request
   */
  createConsole: async ({ commit }, payload) => {
    return new Promise(async (resolve, reject) => {
      commit(types.SET_SAVE_IN_PROGRESS, true);
      try {
        const response = await createDashboard(payload);
        commit(types.SET_SAVE_IN_PROGRESS, false);
        return resolve(response.data);
      } catch (error) {
        commit(types.SET_SAVE_IN_PROGRESS, false);
        return reject(error);
      }
    });
  },
  /**
   * Update console request
   */
  updateConsole: async ({ commit }, payload) => {
    return new Promise(async (resolve, reject) => {
      commit(types.SET_SAVE_IN_PROGRESS, true);
      try {
        const response = await updateDashboard(payload);
        commit(types.SET_SAVE_IN_PROGRESS, false);
        return resolve(response.data);
      } catch (error) {
        commit(types.SET_SAVE_IN_PROGRESS, false);
        return reject(error);
      }
    });
  },

  /**
   * Resets the module state with initial state
   */
  reset: async ({ commit }) => {
    commit(types.reset);
  },
};
